import { Component, Input, Output, EventEmitter, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TabViewModule } from 'primeng/tabview';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, isObservable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserContextService } from '../../../common';

export interface SharedTabConfig {
  id: string; // Unique identifier for the tab
  title: string; // Title to display in the tab header (can be translation key)
  requiredPermission?: string; // Optional: Permission required to view this tab
  component: any; // Component to render when the tab is active
  inputs?: { [key: string]: any }; // Optional: Inputs to pass to the component
  icon?: string; // Optional: Icon to display in the tab header
  disabled?: boolean; // Optional: Whether the tab is disabled
  data?: any; // Optional: Additional data for the tab
}

@Component({
  selector: 'app-shared-tabs',
  standalone: true,
  imports: [CommonModule, TabViewModule, TranslateModule],
  templateUrl: './shared-tabs.component.html',
  styleUrl: './shared-tabs.component.scss'
})
export class SharedTabsComponent {
  private destroyRef = inject(DestroyRef);
  private tabConfigsSignal = signal<SharedTabConfig[]>([]); // Signal to store tab configurations
  private activeTabIndexSignal = signal<number>(0); // Signal to store the active tab index
  private resolvedInputsSignal = signal<{ [key: string]: any }[]>([]); // Signal to store resolved inputs

  @Input() set tabConfigs(value: SharedTabConfig[]) {
    this.tabConfigsSignal.set(value); // Updates the signal
  }
  get tabConfigs(): SharedTabConfig[] {
    return this.tabConfigsSignal(); // Returns the current signal value
  }

  @Input() set activeTabIndex(value: number) {
    this.activeTabIndexSignal.set(value);
  }
  get activeTabIndex(): number {
    return this.activeTabIndexSignal();
  }

  @Output() activeTabChange = new EventEmitter<number>();

  // Computed property to filter tabs based on permissions
  filteredTabs = computed(() => {
    return this.tabConfigsSignal().filter((tab) => {
      // If no permission is required, show the tab
      if (!tab.requiredPermission) {
        return true;
      }
      // If permission is required, check if user has it
      return UserContextService.hasPermission(tab.requiredPermission);
    });
  });

  // Computed property to get the actual tab index based on filtered tabs
  actualTabIndex = computed(() => {
    const filtered = this.filteredTabs();
    const currentIndex = this.activeTabIndexSignal();

    // If current index is within bounds of filtered tabs, return it
    if (currentIndex < filtered.length) {
      return currentIndex;
    }

    // Otherwise, return 0 (first available tab)
    return 0;
  });

  // Computed property to get resolved inputs for filtered tabs
  resolvedInputsForFilteredTabs = computed(() => {
    const filtered = this.filteredTabs();
    const resolved = this.resolvedInputsSignal();
    return filtered.map((tab, index) => resolved[index] || {});
  });

  constructor() {
    // Use effect() to automatically handle input resolution when tab configs change
    effect(() => {
      this.resolveInputs();
    });
  }

  private resolveInputs(): void {
    const tabConfigs = this.tabConfigsSignal();
    const resolvedInputs: { [key: string]: any }[] = [];

    tabConfigs.forEach((tab, index) => {
      if (tab.inputs) {
        const inputKeys = Object.keys(tab.inputs);
        const inputObservables: Observable<any>[] = [];
        const staticInputs: { [key: string]: any } = {};

        // Separate observables from static values
        inputKeys.forEach((key) => {
          const value = tab.inputs![key];
          if (isObservable(value)) {
            inputObservables.push(value.pipe(map((val) => ({ key, value: val }))));
          } else {
            staticInputs[key] = value;
          }
        });

        if (inputObservables.length > 0) {
          // Combine all observable inputs
          const combinedInputs$ = combineLatest(inputObservables).pipe(
            map((results) => {
              const resolvedInput = { ...staticInputs };
              results.forEach((result) => {
                resolvedInput[result.key] = result.value;
              });
              return resolvedInput;
            })
          );

          // Use takeUntilDestroyed for automatic cleanup
          combinedInputs$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((resolvedInput: { [key: string]: any }) => {
              resolvedInputs[index] = resolvedInput;
              this.resolvedInputsSignal.set([...resolvedInputs]);
            });
        } else {
          resolvedInputs[index] = staticInputs;
        }
      } else {
        resolvedInputs[index] = {};
      }
    });

    this.resolvedInputsSignal.set(resolvedInputs);
  }

  onTabChange(event: any) {
    const newIndex = event.index;
    this.activeTabIndexSignal.set(newIndex);
    this.activeTabChange.emit(newIndex);
  }

  // Method to get the actual tab index for a specific tab ID
  getTabIndex(tabId: string): number {
    const filtered = this.filteredTabs();
    return filtered.findIndex((tab) => tab.id === tabId);
  }

  // Method to check if a tab is active
  isTabActive(tabId: string): boolean {
    const filtered = this.filteredTabs();
    const tabIndex = filtered.findIndex((tab) => tab.id === tabId);
    return tabIndex === this.actualTabIndex();
  }

  // Method to get resolved inputs for a specific tab
  getResolvedInputs(tabIndex: number): { [key: string]: any } {
    const resolvedInputs = this.resolvedInputsForFilteredTabs();
    return resolvedInputs[tabIndex] || {};
  }
}
